import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksAccountExpenses,
  useQueryIntegrationsQuickbooksItems
} from "../../../../query/integrations/queries";
import { isQuickbooksServiceActive } from "../../../../query/integrations/selectors";
import { queriesPayers } from "@query";
import { useInvoicesFormContext } from "../InvoicesForm";

export const useQuickbooksProps = ({
  isIntegrationQuickbooksAccounts,
  isIntegrationQuickbooksItems
}: {
  isIntegrationQuickbooksAccounts?: boolean;
  isIntegrationQuickbooksItems?: boolean;
}) => {
  const { watch } = useFormContext();
  const { invoice } = useInvoicesFormContext();
  const payerId = watch("client.payerId");
  // Payer still fetched for parity, but its QBO line-item default is no longer
  // read (see quickbooksDefaultItemId below) — v3 Payer has no payeeOwnedData.
  queriesPayers.getPayer.useQuery(
    { payerId: payerId! },
    { enabled: !!payerId, retry: false }
  );
  const qFeatureFlags = useFeatureFlags();

  const qIntegrationsQuickbooks = useQueryIntegrationsQuickbooks({
    enabled: isIntegrationQuickbooksAccounts || isIntegrationQuickbooksItems
  });

  const isQuickbooksActive =
    qFeatureFlags.data?.qboOnboarding &&
    qIntegrationsQuickbooks.data &&
    (qFeatureFlags.data?.forceShowQBO ||
      isQuickbooksServiceActive(qIntegrationsQuickbooks.data));

  const qIntegrationsQuickbooksItems = useQueryIntegrationsQuickbooksItems({
    enabled: isQuickbooksActive && isIntegrationQuickbooksItems
  });

  const qIntegrationsQuickbooksAccountExpenses =
    useQueryIntegrationsQuickbooksAccountExpenses({
      enabled: isQuickbooksActive && isIntegrationQuickbooksAccounts
    });

  return useMemo(() => {
    const quickbooksOptions = isIntegrationQuickbooksItems
      ? qIntegrationsQuickbooksItems.data?.map(item => ({
          label: item.name,
          value: item.itemId
        }))
      : isIntegrationQuickbooksAccounts
        ? qIntegrationsQuickbooksAccountExpenses.data?.map(item => ({
            label: item.name,
            value: item.accountId
          }))
        : undefined;

    const isQuickbooksItem = isIntegrationQuickbooksItems && isQuickbooksActive;
    const isQuickbooksAccount =
      isIntegrationQuickbooksAccounts && isQuickbooksActive;
    // TODO (v3-migration): the per-invoice and per-payer QBO defaults are both
    // gone — v3 models line-item accounting as `AccountingDimensions` and puts
    // the mapping on the AccountingConnection (PLA-3198 #6). Re-source from
    // there; the account default is all that is available for now.
    const quickbooksDefaultItemId = isQuickbooksItem
      ? qIntegrationsQuickbooks.data?.defaults?.itemId
      : undefined;
    const quickbooksDefaultAccountId = isQuickbooksAccount
      ? qIntegrationsQuickbooks.data?.defaults?.expenseAccountId
      : undefined;

    return {
      isQuickbooksItem,
      isQuickbooksAccount,
      quickbooksOptions: quickbooksOptions || [],
      quickbooksDefaultItemId,
      quickbooksDefaultAccountId
    };
  }, [
    isIntegrationQuickbooksAccounts,
    isIntegrationQuickbooksItems,
    isQuickbooksActive,
    qIntegrationsQuickbooks.data?.defaults?.expenseAccountId,
    qIntegrationsQuickbooks.data?.defaults?.itemId,
    qIntegrationsQuickbooksAccountExpenses.data,
    qIntegrationsQuickbooksItems.data
  ]);
};
